// frontend/src/pages/dashboard/PackagePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { packageAPI, subscriptionAPI, reportAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useMediaQuery,
  useTheme,
  Stack,
  Paper,
  Avatar,
  Tooltip,
  Skeleton,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Folder as FolderIcon,
  Checklist as TaskIcon,
  Storage as StorageIcon,
  Star as StarIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  WorkspacePremium as PremiumIcon,
  Block as BlockIcon
} from '@mui/icons-material';

const PackagePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user, company, refreshUserData } = useAuth();
  
  const [packages, setPackages] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState({
    users: 0,
    projects: 0,
    tasks: 0,
    storageUsed: 0,
    storageLimit: 500
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [downgradeWarning, setDowngradeWarning] = useState(''); // ✅ NEW: Downgrade warning

  // Check if user is admin - redirect if not
  useEffect(() => {
    const isAdmin = user?.teamMembership?.role?.name?.toLowerCase() === 'system administrator';
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadPackagesAndSubscription();
  }, [user, navigate]);

  // Load packages from API, subscription, and usage data
  const loadPackagesAndSubscription = async () => {
    try {
      setLoading(true);
      
      const pkgRes = await packageAPI.getAll();
      setPackages(pkgRes.data || []);

      try {
        const subRes = await subscriptionAPI.getCurrentSubscription();
        setSubscription(subRes.data || null);
      } catch {
        setSubscription(null);
      }

      try {
        const statsRes = await reportAPI.getDashboardStats();
        const stats = statsRes.data || {};
        const storageInfo = stats.storage_info || { used_mb: 0, allowed_mb: 500 };
        
        setUsage({
          users: company?.users_count || stats.total_team_members || 1,
          projects: stats.active_projects || 0,
          tasks: stats.total_tasks || 0,
          storageUsed: storageInfo.used_mb || 0,
          storageLimit: storageInfo.allowed_mb || 500
        });
      } catch {
        // Use default values if API fails
      }
    } catch (err) {
      setError('Failed to load package data');
      console.error('Error loading packages:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if a package is the current active package
  const isCurrentPackage = (pkg) => {
    if (!pkg) return false;
    if (!subscription?.package) return false;
    return subscription.package.id === pkg.id || 
           subscription.package.name?.toLowerCase() === pkg.name.toLowerCase();
  };

  // Format package limits for display
  const getPackageLimits = (pkg) => {
    if (!pkg) {
      return {
        users: 3,
        projects: 1,
        tasks: 20,
        storage: 500 // in MB
      };
    }
    return {
      users: pkg.max_users || 3,
      projects: pkg.max_projects >= 999999 ? 999999 : (pkg.max_projects || 1),
      tasks: pkg.max_tasks >= 999999 ? 999999 : (pkg.max_tasks || 20),
      storage: pkg.storage_limit || 500 // in MB
    };
  };

  // ✅ NEW: Check if downgrade would violate usage limits
  const canDowngrade = (pkg) => {
    const limits = getPackageLimits(pkg);
    
    // Check each limit
    if (usage.users > limits.users) {
      return { allowed: false, reason: `You have ${usage.users} users, but this package allows only ${limits.users}.` };
    }
    if (usage.projects > limits.projects && limits.projects < 999999) {
      return { allowed: false, reason: `You have ${usage.projects} active projects, but this package allows only ${limits.projects}.` };
    }
    if (usage.tasks > limits.tasks && limits.tasks < 999999) {
      return { allowed: false, reason: `You have ${usage.tasks} tasks, but this package allows only ${limits.tasks} per project.` };
    }
    if (usage.storageUsed > limits.storage) {
      return { allowed: false, reason: `You are using ${usage.storageUsed}MB storage, but this package allows only ${limits.storage}MB.` };
    }
    
    return { allowed: true, reason: '' };
  };

  // Calculate usage percentage for progress bars
  const getUsagePercent = (used, limit) => {
    if (!limit || limit === 'Unlimited' || limit >= 999999) return 0;
    const numLimit = typeof limit === 'number' ? limit : parseFloat(limit);
    if (!numLimit || numLimit <= 0) return 0;
    return Math.min(100, Math.round((used / numLimit) * 100));
  };

  // Handle package selection for purchase
  const handlePurchase = (pkg) => {
    if (!pkg || isCurrentPackage(pkg)) return;
    
    // ✅ Check downgrade restrictions
    const downgradeCheck = canDowngrade(pkg);
    if (!downgradeCheck.allowed) {
      setDowngradeWarning(downgradeCheck.reason);
      setSelectedPackage(pkg);
      setOpenDialog(true);
      return;
    }
    
    setDowngradeWarning('');
    setSelectedPackage(pkg);
    setOpenDialog(true);
  };

  // Confirm package purchase/activation
  const confirmPurchase = async () => {
    if (!selectedPackage) return;
    
    // ✅ Final check before purchase
    const downgradeCheck = canDowngrade(selectedPackage);
    if (!downgradeCheck.allowed) {
      setError(downgradeCheck.reason);
      return;
    }
    
    try {
      const response = await subscriptionAPI.purchasePackage(selectedPackage.id);
      setSuccess(response.data.message || 'Package activated successfully!');
      
      if (refreshUserData) {
        await refreshUserData();
      }
      await loadPackagesAndSubscription();
      
      setTimeout(() => {
        // Redirect to external website instead of dashboard
        window.location.href = 'https://softsolutionmedia.gumroad.com/l/syncro';
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate package');
      console.error('Error:', err);
    } finally {
      setOpenDialog(false);
      setSelectedPackage(null);
      setDowngradeWarning('');
    }
  };

  // Show loading state
  if (loading || !user) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  // Admin check - return null if not admin
  const isAdmin = user?.teamMembership?.role?.name?.toLowerCase() === 'system administrator';
  if (!isAdmin) {
    return null;
  }

  // Get current package info
  const currentPkg = packages?.find(p => isCurrentPackage(p));
  const currentLimits = currentPkg ? getPackageLimits(currentPkg) : getPackageLimits(null);

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        
        {/* Current Package Banner */}
        {currentPkg && (
          <Paper
            variant="outlined"
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              border: `2px solid ${theme.palette.success.main}`,
              bgcolor: `${theme.palette.success.main}08`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Avatar sx={{ bgcolor: theme.palette.success.main, width: 48, height: 48 }}>
                <PremiumIcon sx={{ color: 'white' }} />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  Current Plan: {currentPkg.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentPkg.description}
                </Typography>
              </Box>
              <Chip 
                label="Active" 
                color="success" 
                size="small"
                icon={<CheckIcon />}
              />
            </Box>
            
            {/* Usage Stats Grid */}
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
              {/* Users Usage */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <PeopleIcon fontSize="small" color="primary" />
                  <Typography variant="caption" fontWeight={600}>Users</Typography>
                </Box>
                <Typography variant="h6" fontWeight={700}>
                  {usage.users} / {currentLimits?.users}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={getUsagePercent(usage.users, currentLimits?.users)}
                  sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                  color={getUsagePercent(usage.users, currentLimits?.users) >= 90 ? 'error' : 'primary'}
                />
              </Box>
              
              {/* Projects Usage */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <FolderIcon fontSize="small" color="primary" />
                  <Typography variant="caption" fontWeight={600}>Active Projects</Typography>
                </Box>
                <Typography variant="h6" fontWeight={700}>
                  {usage.projects} / {currentLimits?.projects}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={currentLimits?.projects !== 'Unlimited' ? getUsagePercent(usage.projects, currentLimits?.projects) : 0}
                  sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                  color={currentLimits?.projects !== 'Unlimited' && getUsagePercent(usage.projects, currentLimits?.projects) >= 90 ? 'error' : 'primary'}
                />
              </Box>
              
              {/* Tasks Usage */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <TaskIcon fontSize="small" color="primary" />
                  <Typography variant="caption" fontWeight={600}>Tasks/Project</Typography>
                </Box>
                <Typography variant="h6" fontWeight={700}>
                  {usage.tasks} / {currentLimits?.tasks}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={currentLimits?.tasks !== 'Unlimited' ? getUsagePercent(usage.tasks, currentLimits?.tasks) : 0}
                  sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                  color={currentLimits?.tasks !== 'Unlimited' && getUsagePercent(usage.tasks, currentLimits?.tasks) >= 90 ? 'error' : 'primary'}
                />
              </Box>
              
              {/* Storage Usage */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <StorageIcon fontSize="small" color="primary" />
                  <Typography variant="caption" fontWeight={600}>Storage</Typography>
                </Box>
                <Typography variant="h6" fontWeight={700}>
                  {usage.storageUsed.toFixed(0)}MB / {currentLimits?.storage}MB
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={getUsagePercent(usage.storageUsed, currentPkg?.storage_limit)}
                  sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                  color={getUsagePercent(usage.storageUsed, currentPkg?.storage_limit) >= 90 ? 'error' : 'primary'}
                />
              </Box>
            </Box>
            
            {/* Warning alert if nearing any limit */}
            {(getUsagePercent(usage.users, currentLimits?.users) >= 90 ||
              (currentLimits?.projects !== 'Unlimited' && getUsagePercent(usage.projects, currentLimits?.projects) >= 90) ||
              getUsagePercent(usage.storageUsed, currentPkg?.storage_limit) >= 90) && (
              <Alert 
                severity="warning" 
                sx={{ mt: 2, borderRadius: 1 }}
                icon={<WarningIcon />}
              >
                <Typography variant="body2" fontWeight={500}>
                  You are nearing your package limits. Consider upgrading to continue growing.
                </Typography>
              </Alert>
            )}
          </Paper>
        )}

        {/* Page Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} gutterBottom>
            Choose Your Plan
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select the perfect package for your team's needs
          </Typography>
        </Box>

        {/* Error and Success Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}

        {/* Package Cards Grid */}
        <Grid container spacing={3} justifyContent="center">
          {(packages || []).map((pkg) => {
            if (!pkg) return null;
            const current = isCurrentPackage(pkg);
            const limits = getPackageLimits(pkg);
            const downgradeCheck = canDowngrade(pkg);
            const cannotDowngrade = !downgradeCheck.allowed && !current;
            
            return (
              <Grid item size={{ xs:12, sm:6, md:3 }} key={pkg.id || pkg.name}>
                <Paper
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 1,
                    transition: 'all 0.3s ease',
                    border: current ? `3px solid ${theme.palette.success.main}` : undefined,
                    opacity: cannotDowngrade ? 0.7 : 1,
                    '&:hover': {
                      transform: isMobile ? 'none' : cannotDowngrade ? 'none' : 'translateY(-8px)',
                      boxShadow: cannotDowngrade ? 1 : (current ? 2 : 3)
                    },
                    position: 'relative',
                    overflow: 'visible', 
                    ...(pkg.is_popular && !current && {
                      border: `2px solid ${theme.palette.primary.main}`,
                      '&::before': {
                        content: '"Recommended"',
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 20,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        zIndex: 1
                      }
                    }),
                    ...(current && {
                      '&::before': {
                        content: '"Current Plan"',
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: theme.palette.success.main,
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 20,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        zIndex: 1
                      }
                    })
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    {/* Package Header */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          mx: 'auto',
                          mb: 1,
                          bgcolor: pkg.is_popular ? theme.palette.primary.main : theme.palette.grey[300]
                        }}
                      >
                        {pkg.price === 0 ? (
                          <StarIcon sx={{ color: pkg.is_popular ? 'white' : 'text.primary', fontSize: 28 }} />
                        ) : (
                          <MoneyIcon sx={{ color: pkg.is_popular ? 'white' : 'text.primary', fontSize: 28 }} />
                        )}
                      </Avatar>
                      <Typography variant="h6" component="div" fontWeight={700} gutterBottom>
                        {pkg.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" paragraph sx={{ display: 'block' }}>
                        {pkg.description}
                      </Typography>
                    </Box>

                    {/* Package Price */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="h4" color="primary" fontWeight={700}>
                        {pkg.price === 0 ? 'Free' : `$${pkg.price}`}
                      </Typography>
                      {pkg.price > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          per month
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Package Limits */}
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon fontSize="small" color="primary" />
                        <Typography variant="caption">
                          <strong>{limits.users}</strong> User{limits.users !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon fontSize="small" color="primary" />
                        <Typography variant="caption">
                          <strong>{limits.projects}</strong> Active Project{limits.projects !== 1 && limits.projects !== 'Unlimited' ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StorageIcon fontSize="small" color="primary" />
                        <Typography variant="caption">
                          <strong>{limits.storage}MB</strong> Storage
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TaskIcon fontSize="small" color="primary" />
                        <Typography variant="caption">
                          <strong>{limits.tasks}</strong> Tasks/Project
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Package Features */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {(pkg.features || []).slice(0, 4).map((feature, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckIcon fontSize="small" sx={{ color: theme.palette.success.main, fontSize: 14 }} />
                          <Typography variant="caption" color="text.secondary">
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                      {(pkg.features || []).length > 4 && (
                        <Tooltip title={(pkg.features || []).slice(4).join(', ')}>
                          <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 500 }}>
                            +{(pkg.features || []).length - 4} more features
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>

                  {/* Package Action Button */}
                  <CardActions sx={{ p: 2, justifyContent: 'center', pt: 0 }}>
                    {cannotDowngrade ? (
                      <Tooltip title={downgradeCheck.reason}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled
                            sx={{
                              width: '100%',
                              fontWeight: 600,
                              py: 1,
                              fontSize: '0.875rem',
                              borderColor: 'error.main',
                              color: 'error.main'
                            }}
                            startIcon={<BlockIcon />}
                          >
                            Cannot Downgrade
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        size="small"
                        variant={current ? "outlined" : (pkg.is_popular ? "contained" : "outlined")}
                        onClick={() => handlePurchase(pkg)}
                        disabled={current}
                        sx={{
                          height: 48, // Slightly taller for better touch target
                          px: 3,
                          borderRadius: 3, // More rounded corners (pill-like)
                          fontWeight: 600,
                          color: '#fff',
                          letterSpacing: '0.5px',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          boxShadow: '0 4px 15px rgba(0,0,0,0.15)', 
                          '&:hover': {
                            boxShadow: '0 8px 25px rgba(0,0,0,0.25)', // Lift effect
                            transform: 'translateY(-1px)',
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        {current ? '✓ Current Plan' : (pkg.price === 0 ? 'Activate Free' : pkg.is_popular ? 'Get Started' : 'Choose Plan')}
                      </Button>
                    )}
                  </CardActions>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Purchase Confirmation Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            {selectedPackage?.price === 0 ? 'Activate Free Plan' : 'Confirm Purchase'}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: theme.palette.primary.main
                }}
              >
                <MoneyIcon sx={{ color: 'white', fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" color="primary" gutterBottom>
                {selectedPackage?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {selectedPackage?.description}
              </Typography>
              <Typography variant="h4" color="primary" fontWeight={700} gutterBottom>
                {selectedPackage?.price === 0 ? 'Free' : `$${selectedPackage?.price}/month`}
              </Typography>
            </Box>

            {/* ✅ Downgrade Warning */}
            {downgradeWarning && (
              <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                <Typography variant="body2" fontWeight={500}>
                  ⚠️ {downgradeWarning}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Please reduce your usage or choose a higher package to proceed.
                </Typography>
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                What is included:
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon fontSize="small" color="success" />
                  <Typography variant="body2">
                    <strong>{getPackageLimits(selectedPackage).users}</strong> User{getPackageLimits(selectedPackage).users !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon fontSize="small" color="success" />
                  <Typography variant="body2">
                    <strong>{getPackageLimits(selectedPackage).projects}</strong> Active Project{getPackageLimits(selectedPackage).projects !== 1 && getPackageLimits(selectedPackage).projects !== 'Unlimited' ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon fontSize="small" color="success" />
                  <Typography variant="body2">
                    <strong>{getPackageLimits(selectedPackage).storage}MB</strong> Total Storage
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon fontSize="small" color="success" />
                  <Typography variant="body2">
                    <strong>{getPackageLimits(selectedPackage).tasks}</strong> Tasks per Project
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Features section */}
            {(selectedPackage?.features || []).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Additional Features:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {(selectedPackage.features || []).map((feature, idx) => (
                    <Chip
                      key={idx}
                      label={feature}
                      size="small"
                      icon={<CheckIcon fontSize="small" />}
                      variant="outlined"
                      sx={{
                        fontWeight: 500,
                        borderColor: 'divider',
                        fontSize: '0.75rem',
                        height: 28,
                        '& .MuiChip-icon': {
                          color: theme.palette.success.main
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2, fontSize: '0.875rem' }} icon={<InfoIcon fontSize="inherit" />}>
              <strong>Note:</strong> Active projects are counted (completed projects do not count). Storage is calculated from all attachments across your projects.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={confirmPurchase} 
              variant="contained" 
              size="large"
              disabled={!!downgradeWarning} // ✅ Disable if downgrade warning exists
            >
              {selectedPackage?.price === 0 ? 'Activate Free' : 'Confirm & Subscribe'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default PackagePage;